import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';

/**
 * SuperadminRoute - Protects routes that require superadmin access
 * 
 * Usage:
 * <SuperadminRoute>
 *   <PlatformDashboard />
 * </SuperadminRoute>
 * 
 * With permission check:
 * <SuperadminRoute requiredPermission="tenants:write">
 *   <TenantManagement />
 * </SuperadminRoute>
 */
const SuperadminRoute = ({ children, requiredPermission = null }) => {
  const { isSuperadmin, hasSuperadminPermission, loading } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not a superadmin - redirect to login (not dashboard, as user may not have tenant)
  if (!isSuperadmin) {
    console.log('🚫 Access denied: Not a superadmin');
    return <Navigate to="/login" replace />;
  }

  // Check specific permission if required
  if (requiredPermission && !hasSuperadminPermission(requiredPermission)) {
    console.log(`🚫 Access denied: Missing permission ${requiredPermission}`);
    // Redirect to platform admin home (they can see what they have access to)
    return <Navigate to="/platform-admin" replace />;
  }

  // Authorized - render children
  return children;
};

export default SuperadminRoute;
